// The transport gesture, with no React and no DOM in it.
//
// Split out from the hook because this is the part with behaviour worth pinning down: a
// hold that has already fired must swallow the tap on release, a press that wanders off the
// button must do nothing at all, and a second press must not inherit the first one's timer.
// All three are easy to get subtly wrong and none of them need a browser to check.

/** How long a press has to last before it counts as a hold rather than a tap. */
export const HOLD_MS = 400;

export interface Timers {
  set(callback: () => void, ms: number): number;
  clear(handle: number): void;
}

export const windowTimers: Timers = {
  set: (callback, ms) => window.setTimeout(callback, ms),
  clear: (handle) => window.clearTimeout(handle),
};

export interface TapHold {
  down(): void;
  up(): void;
  cancel(): void;
  dispose(): void;
}

export function createTapHold(options: {
  onTap: () => void;
  onHold: () => void;
  holdMs?: number;
  timers?: Timers;
}): TapHold {
  const { onTap, onHold, holdMs = HOLD_MS, timers = windowTimers } = options;
  let handle: number | null = null;
  let held = false;
  // Whether a gesture is in flight at all. Distinct from `held`: a cancelled press is
  // neither a tap nor a hold, and the release that follows it has to fire nothing. Without
  // this, sliding off the button and letting go still seeks.
  let active = false;

  const clear = () => {
    if (handle !== null) {
      timers.clear(handle);
      handle = null;
    }
  };

  return {
    down() {
      // A press arriving while one is already in flight replaces it rather than stacking:
      // a stray pointerdown would otherwise leave a timer that fires under the next press.
      clear();
      held = false;
      active = true;
      handle = timers.set(() => {
        handle = null;
        held = true;
        // Fires while the finger is still down, so the gesture describes itself. Waiting
        // for release would make a long press indistinguishable from a slow tap until it
        // was already over.
        onHold();
      }, holdMs);
    },

    up() {
      clear();
      if (active && !held) onTap();
      held = false;
      active = false;
    },

    cancel() {
      // Sliding off the button abandons the gesture instead of committing to the tap, so a
      // mis-aimed press does nothing rather than seeking unexpectedly.
      clear();
      held = false;
      active = false;
    },

    dispose() {
      clear();
    },
  };
}
